import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../bizsuite-app';
import Modal from '../components/Modal';
import FormField from '../components/FormField';
import { useFormDirty } from '../hooks/useFormDirty';
import { formatDate } from '../utils/date';

const STATUS_BADGES = { pending: 'badge-yellow', approved: 'badge-green', rejected: 'badge-red', submitted: 'badge-blue' };
const TABS = ['Dashboard', 'Attendance', 'WFH Requests', 'Travel', 'Leave', 'My Profile'];
const EXPENSE_TYPES = ['flight', 'hotel', 'taxi', 'food', 'other'];
const LEAVE_TYPES = ['Casual Leave', 'Sick Leave', 'Earned Leave', 'Comp Off', 'Loss of Pay', 'Maternity Leave', 'Paternity Leave'];
const WORK_MODES = [
  { value: 'office', label: 'Office' },
  { value: 'wfh', label: 'Work from Home' },
  { value: 'client_site', label: 'Client Site' },
  { value: 'travel', label: 'Travel' },
];

const ATT_COLORS = {
  present: '#10B981',
  wfh: '#3B82F6',
  leave: '#F59E0B',
  absent: '#EF4444',
  half_day: '#F97316',
  holiday: '#9CA3AF',
  weekend: '#D1D5DB',
};

function formatTime(d) {
  if (!d) return '--';
  return new Date(d).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatCurrency(amt, currency) {
  const num = parseFloat(amt);
  if (isNaN(num)) return '--';
  return `${currency || 'INR'} ${num.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

export default function ESS() {
  const [activeTab, setActiveTab] = useState(0);
  const [dashboardData, setDashboardData] = useState(null);
  const [attendance, setAttendance] = useState([]);
  const [attSummary, setAttSummary] = useState(null);
  const [wfhRequests, setWfhRequests] = useState([]);
  const [teamWfh, setTeamWfh] = useState([]);
  const [travelRequests, setTravelRequests] = useState([]);
  const [travelExpenses, setTravelExpenses] = useState([]);
  const [selectedTravel, setSelectedTravel] = useState(null);
  const [leaveBalances, setLeaveBalances] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [profile, setProfile] = useState(null);
  const [profileSkills, setProfileSkills] = useState([]);
  const [profileCerts, setProfileCerts] = useState([]);
  const [profileAllocations, setProfileAllocations] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [wfTiles, setWfTiles] = useState([]);
  const [wfTileForm, setWfTileForm] = useState(null); // { workflow, formDef } when tile clicked
  const [wfTileValues, setWfTileValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  // Modals
  const [wfhModal, setWfhModal] = useState(false);
  const [travelModal, setTravelModal] = useState(false);
  const [expenseModal, setExpenseModal] = useState(false);
  const [leaveModal, setLeaveModal] = useState(false);
  const [profileEditModal, setProfileEditModal] = useState(false);

  // Forms
  const [wfhForm, setWfhForm] = useState({ fromDate: '', toDate: '', days: '', reason: '' });
  const [travelForm, setTravelForm] = useState({ destination: '', purpose: '', fromDate: '', toDate: '', estimatedCost: '', currency: 'INR', projectId: '' });
  const [expenseForm, setExpenseForm] = useState({ expenseType: 'other', description: '', amount: '', date: '', receiptUrl: '' });
  const [leaveForm, setLeaveForm] = useState({ leaveType: 'Casual Leave', fromDate: '', toDate: '', days: '', reason: '' });
  const [profileForm, setProfileForm] = useState({ phone: '', email: '' });

  const { dirty, markDirty, resetDirty } = useFormDirty();

  // Date range for attendance
  const now = new Date();
  const [attMonth, setAttMonth] = useState(now.getMonth() + 1);
  const [attYear, setAttYear] = useState(now.getFullYear());

  // ===================== Fetch Functions =====================

  const fetchDashboard = useCallback(async () => {
    try {
      const [dashRes, tileRes] = await Promise.all([
        apiFetch('/api/ess/dashboard'),
        apiFetch('/api/workflow/tiles/page/self-service'),
      ]);
      if (dashRes.ok) {
        const data = await dashRes.json();
        setDashboardData(data);
        setAnnouncements(data.announcements || []);
      }
      if (tileRes.ok) {
        const tData = await tileRes.json();
        setWfTiles(tData.tiles || []);
      }
    } catch (err) { /* handled */ }
    setLoading(false);
  }, []);

  const fetchAttendance = useCallback(async () => {
    const fromDate = `${attYear}-${String(attMonth).padStart(2, '0')}-01`;
    const lastDay = new Date(attYear, attMonth, 0).getDate();
    const toDate = `${attYear}-${String(attMonth).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    try {
      const [attRes, sumRes] = await Promise.all([
        apiFetch(`/api/ess/attendance?fromDate=${fromDate}&toDate=${toDate}`),
        apiFetch(`/api/ess/attendance/summary?month=${attMonth}&year=${attYear}`),
      ]);
      if (attRes.ok) { const d = await attRes.json(); setAttendance(d.attendance || []); }
      if (sumRes.ok) { const d = await sumRes.json(); setAttSummary(d.summary || null); }
    } catch (err) { /* handled */ }
  }, [attMonth, attYear]);

  const fetchWfh = useCallback(async () => {
    try {
      const [myRes, teamRes] = await Promise.all([
        apiFetch('/api/ess/wfh'),
        apiFetch('/api/ess/wfh/team'),
      ]);
      if (myRes.ok) { const d = await myRes.json(); setWfhRequests(d.requests || []); }
      if (teamRes.ok) { const d = await teamRes.json(); setTeamWfh(d.requests || []); }
    } catch (err) { /* handled */ }
  }, []);

  const fetchTravel = useCallback(async () => {
    try {
      const res = await apiFetch('/api/ess/travel');
      if (res.ok) { const d = await res.json(); setTravelRequests(d.requests || []); }
    } catch (err) { /* handled */ }
  }, []);

  const fetchTravelExpenses = useCallback(async (travelId) => {
    if (!travelId) { setTravelExpenses([]); return; }
    try {
      const res = await apiFetch(`/api/ess/travel/${travelId}/expenses`);
      if (res.ok) { const d = await res.json(); setTravelExpenses(d.expenses || []); }
    } catch (err) { /* handled */ }
  }, []);

  const fetchLeave = useCallback(async () => {
    try {
      const [balRes, reqRes] = await Promise.all([
        apiFetch('/api/leave/balances?mine=true'),
        apiFetch('/api/leave/requests'),
      ]);
      if (balRes.ok) { const d = await balRes.json(); setLeaveBalances(d.balances || []); }
      if (reqRes.ok) { const d = await reqRes.json(); setLeaveRequests(d.requests || []); }
    } catch (err) { /* handled */ }
  }, []);

  const fetchProfile = useCallback(async () => {
    try {
      const res = await apiFetch('/api/ess/profile');
      if (res.ok) {
        const d = await res.json();
        setProfile(d.profile || null);
        setProfileSkills(d.skills || []);
        setProfileCerts(d.certifications || []);
        setProfileAllocations(d.allocations || []);
        if (d.profile) {
          setProfileForm({ phone: d.profile.phone || '', email: d.profile.email || '' });
        }
      }
    } catch (err) { /* handled */ }
  }, []);

  // Load data when tab changes
  useEffect(() => {
    setLoading(true);
    if (activeTab === 0) fetchDashboard();
    else if (activeTab === 1) { fetchAttendance(); setLoading(false); }
    else if (activeTab === 2) { fetchWfh(); setLoading(false); }
    else if (activeTab === 3) { fetchTravel(); setLoading(false); }
    else if (activeTab === 4) { fetchLeave(); setLoading(false); }
    else if (activeTab === 5) { fetchProfile(); setLoading(false); }
    else setLoading(false);
  }, [activeTab, fetchDashboard, fetchAttendance, fetchWfh, fetchTravel, fetchLeave, fetchProfile]);

  // ===================== Action Handlers =====================

  async function handleCheckIn() {
    setSaving(true);
    try {
      const res = await apiFetch('/api/ess/attendance/check-in', { method: 'POST', body: JSON.stringify({ workMode: 'office' }) });
      if (res.ok) { fetchDashboard(); fetchAttendance(); }
      else { const d = await res.json(); setErrors({ _general: d.error }); }
    } catch (err) { setErrors({ _general: 'Network error' }); }
    setSaving(false);
  }

  async function handleCheckOut() {
    setSaving(true);
    try {
      const res = await apiFetch('/api/ess/attendance/check-out', { method: 'POST' });
      if (res.ok) { fetchDashboard(); fetchAttendance(); }
      else { const d = await res.json(); setErrors({ _general: d.error }); }
    } catch (err) { setErrors({ _general: 'Network error' }); }
    setSaving(false);
  }

  async function handleWfhSubmit() {
    const errs = {};
    if (!wfhForm.fromDate) errs.fromDate = 'Required';
    if (!wfhForm.toDate) errs.toDate = 'Required';
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSaving(true);
    try {
      const res = await apiFetch('/api/ess/wfh', { method: 'POST', body: JSON.stringify(wfhForm) });
      if (res.ok) {
        resetDirty();
        setWfhModal(false);
        setWfhForm({ fromDate: '', toDate: '', days: '', reason: '' });
        fetchWfh();
      } else { const d = await res.json(); setErrors({ _general: d.error }); }
    } catch (err) { setErrors({ _general: 'Network error' }); }
    setSaving(false);
  }

  async function handleWfhApprove(id) {
    try { await apiFetch(`/api/ess/wfh/${id}/approve`, { method: 'POST' }); fetchWfh(); } catch (err) { /* ignore */ }
  }

  async function handleWfhReject(id) {
    const reason = prompt('Rejection reason (optional):');
    try { await apiFetch(`/api/ess/wfh/${id}/reject`, { method: 'POST', body: JSON.stringify({ reason }) }); fetchWfh(); } catch (err) { /* ignore */ }
  }

  async function handleWfhCancel(id) {
    try { await apiFetch(`/api/ess/wfh/${id}`, { method: 'DELETE' }); fetchWfh(); } catch (err) { /* ignore */ }
  }

  async function handleTravelSubmit() {
    const errs = {};
    if (!travelForm.destination) errs.destination = 'Required';
    if (!travelForm.fromDate) errs.fromDate = 'Required';
    if (!travelForm.toDate) errs.toDate = 'Required';
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSaving(true);
    try {
      const res = await apiFetch('/api/ess/travel', { method: 'POST', body: JSON.stringify(travelForm) });
      if (res.ok) {
        resetDirty();
        setTravelModal(false);
        setTravelForm({ destination: '', purpose: '', fromDate: '', toDate: '', estimatedCost: '', currency: 'INR', projectId: '' });
        fetchTravel();
      } else { const d = await res.json(); setErrors({ _general: d.error }); }
    } catch (err) { setErrors({ _general: 'Network error' }); }
    setSaving(false);
  }

  async function handleTravelApprove(id) {
    try { await apiFetch(`/api/ess/travel/${id}/approve`, { method: 'POST' }); fetchTravel(); } catch (err) { /* ignore */ }
  }

  async function handleTravelReject(id) {
    const reason = prompt('Rejection reason (optional):');
    try { await apiFetch(`/api/ess/travel/${id}/reject`, { method: 'POST', body: JSON.stringify({ reason }) }); fetchTravel(); } catch (err) { /* ignore */ }
  }

  async function handleExpenseSubmit() {
    if (!selectedTravel) return;
    const errs = {};
    if (!expenseForm.expenseType) errs.expenseType = 'Required';
    if (!expenseForm.amount) errs.amount = 'Required';
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSaving(true);
    try {
      const res = await apiFetch(`/api/ess/travel/${selectedTravel.id}/expenses`, {
        method: 'POST', body: JSON.stringify(expenseForm),
      });
      if (res.ok) {
        resetDirty();
        setExpenseModal(false);
        setExpenseForm({ expenseType: 'other', description: '', amount: '', date: '', receiptUrl: '' });
        fetchTravelExpenses(selectedTravel.id);
      } else { const d = await res.json(); setErrors({ _general: d.error }); }
    } catch (err) { setErrors({ _general: 'Network error' }); }
    setSaving(false);
  }

  async function handleDeleteExpense(expId) {
    if (!selectedTravel) return;
    try {
      await apiFetch(`/api/ess/travel/${selectedTravel.id}/expenses/${expId}`, { method: 'DELETE' });
      fetchTravelExpenses(selectedTravel.id);
    } catch (err) { /* ignore */ }
  }

  async function handleSubmitExpenses(travelId) {
    setSaving(true);
    try {
      const res = await apiFetch(`/api/ess/travel/${travelId}/submit-expenses`, { method: 'POST' });
      if (res.ok) {
        const d = await res.json();
        alert(`Reimbursement ${d.reimbursementNumber} created for ${formatCurrency(d.totalAmount, 'INR')}`);
        fetchTravel();
      } else { const d = await res.json(); alert(d.error || 'Failed'); }
    } catch (err) { alert('Network error'); }
    setSaving(false);
  }

  async function handleLeaveSubmit() {
    const errs = {};
    if (!leaveForm.fromDate) errs.fromDate = 'Required';
    if (!leaveForm.toDate) errs.toDate = 'Required';
    if (!leaveForm.days) errs.days = 'Required';
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSaving(true);
    try {
      const res = await apiFetch('/api/leave/request', { method: 'POST', body: JSON.stringify(leaveForm) });
      if (res.ok) {
        resetDirty();
        setLeaveModal(false);
        setLeaveForm({ leaveType: 'Casual Leave', fromDate: '', toDate: '', days: '', reason: '' });
        fetchLeave();
      } else { const d = await res.json(); setErrors({ _general: d.error }); }
    } catch (err) { setErrors({ _general: 'Network error' }); }
    setSaving(false);
  }

  async function handleLeaveApprove(id) {
    try { await apiFetch(`/api/leave/request/${id}/approve`, { method: 'POST' }); fetchLeave(); } catch (err) { /* ignore */ }
  }

  async function handleLeaveReject(id) {
    try { await apiFetch(`/api/leave/request/${id}/reject`, { method: 'POST' }); fetchLeave(); } catch (err) { /* ignore */ }
  }

  async function handleProfileUpdate() {
    setSaving(true);
    try {
      const res = await apiFetch('/api/ess/profile', { method: 'PUT', body: JSON.stringify(profileForm) });
      if (res.ok) { resetDirty(); setProfileEditModal(false); fetchProfile(); }
      else { const d = await res.json(); setErrors({ _general: d.error }); }
    } catch (err) { setErrors({ _general: 'Network error' }); }
    setSaving(false);
  }

  function autoCalcDays(form, setFn, fromKey, toKey) {
    return (name, value) => {
      const next = { ...form, [name]: value };
      setFn(next);
      markDirty();
      const f = next[fromKey];
      const t = next[toKey];
      if (f && t) {
        const diff = Math.ceil((new Date(t) - new Date(f)) / 86400000) + 1;
        if (diff > 0) setFn(prev => ({ ...prev, days: diff.toString() }));
      }
    };
  }

  // ===================== Calendar Builder =====================

  function buildCalendar() {
    const daysInMonth = new Date(attYear, attMonth, 0).getDate();
    const firstDow = new Date(attYear, attMonth - 1, 1).getDay();
    const attMap = {};
    attendance.forEach(a => { attMap[a.date] = a; });

    const cells = [];
    for (let i = 0; i < firstDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${attYear}-${String(attMonth).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const att = attMap[dateStr];
      const dow = new Date(attYear, attMonth - 1, d).getDay();
      let color = '#F3F4F6';
      let label = '';
      if (dow === 0 || dow === 6) { color = ATT_COLORS.weekend; label = 'W'; }
      else if (att) {
        if (att.status === 'leave') { color = ATT_COLORS.leave; label = 'L'; }
        else if (att.status === 'absent') { color = ATT_COLORS.absent; label = 'A'; }
        else if (att.status === 'half_day') { color = ATT_COLORS.half_day; label = 'H'; }
        else if (att.status === 'holiday') { color = ATT_COLORS.holiday; label = 'HO'; }
        else if (att.work_mode === 'wfh') { color = ATT_COLORS.wfh; label = 'WFH'; }
        else { color = ATT_COLORS.present; label = 'P'; }
      }
      cells.push({ day: d, color, label, att, dateStr });
    }
    return cells;
  }

  // ===================== Render Helpers =====================

  function renderStatCard(label, value, color, icon) {
    return (
      <div className="card" style={{ flex: '1 0 140px', maxWidth: 200 }}>
        <div className="card-body" style={{ padding: 16, textAlign: 'center' }}>
          <div style={{ fontSize: 28 }}>{icon}</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: color || '#1E293B' }}>{value}</div>
          <div style={{ fontSize: 12, color: '#64748B' }}>{label}</div>
        </div>
      </div>
    );
  }

  function renderErrorBanner() {
    if (!errors._general) return null;
    return (
      <div style={{ background: '#FEF2F2', color: '#DC2626', padding: '10px 14px', borderRadius: 6, marginBottom: 16, fontSize: 13 }}>
        {errors._general}
        <button style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', color: '#DC2626' }} onClick={() => setErrors({})}>x</button>
      </div>
    );
  }

  // ===================== TAB RENDERS =====================

  function renderDashboard() {
    if (!dashboardData) return <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner"></div></div>;
    const emp = dashboardData.employee;
    const todayAtt = dashboardData.todayAttendance;
    const summ = dashboardData.attendanceSummary || {};
    const pending = dashboardData.pendingRequests || {};
    const balances = dashboardData.leaveBalances || [];

    return (
      <div>
        {renderErrorBanner()}
        {/* Welcome */}
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-body" style={{ padding: 20 }}>
            <h2 style={{ margin: 0, color: '#1E293B' }}>
              Welcome, {emp ? `${emp.firstName} ${emp.lastName}` : 'Employee'}
            </h2>
            <div style={{ color: '#64748B', fontSize: 14, marginTop: 4 }}>
              {emp && emp.designation ? `${emp.designation} | ${emp.department || ''}` : ''}
              {' | '}{new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          <div className="card" style={{ flex: '1 0 280px' }}>
            <div className="card-body" style={{ padding: 20 }}>
              <h3 style={{ marginTop: 0, marginBottom: 12, fontSize: 14, color: '#64748B' }}>Quick Actions</h3>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {(!todayAtt || !todayAtt.check_in) ? (
                  <button className="btn btn-primary" onClick={handleCheckIn} disabled={saving}>Check In</button>
                ) : !todayAtt.check_out ? (
                  <button className="btn btn-primary" style={{ background: '#EF4444' }} onClick={handleCheckOut} disabled={saving}>Check Out</button>
                ) : (
                  <span style={{ color: '#10B981', fontWeight: 600, fontSize: 14 }}>Day complete - {todayAtt.hours_worked || 0}h worked</span>
                )}
                <button className="btn btn-secondary" onClick={() => { setActiveTab(2); resetDirty(); setWfhModal(true); }}>Request WFH</button>
                <button className="btn btn-secondary" onClick={() => { setActiveTab(4); resetDirty(); setLeaveModal(true); }}>Apply Leave</button>
              </div>
              {todayAtt && todayAtt.check_in && !todayAtt.check_out && (
                <div style={{ marginTop: 8, fontSize: 13, color: '#64748B' }}>
                  Checked in at {formatTime(todayAtt.check_in)}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Workflow Tiles */}
        {wfTiles.length > 0 && (
          <div className="card" style={{ marginBottom: 20 }}>
            <div className="card-header"><h2>Quick Requests</h2></div>
            <div className="card-body" style={{ padding: 16 }}>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {wfTiles.map(tile => (
                  <button key={tile.id}
                    onClick={async () => {
                      if (!tile.form_id) return;
                      try {
                        const fRes = await apiFetch(`/api/workflow/forms/${tile.form_id}`);
                        if (fRes.ok) {
                          const formDef = await fRes.json();
                          setWfTileForm({ tile, formDef, workflow_id: tile.workflow_id });
                          setWfTileValues({});
                        }
                      } catch (e) { /* handled */ }
                    }}
                    style={{
                      padding: '16px 24px', borderRadius: 10, cursor: 'pointer',
                      border: `1px solid ${tile.color || '#00e0ff'}33`,
                      background: `${tile.color || '#00e0ff'}08`,
                      color: tile.color || '#00e0ff', textAlign: 'center',
                      minWidth: 140, transition: 'all 0.2s',
                    }}>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{tile.title}</div>
                    {tile.subtitle && <div style={{ fontSize: 11, opacity: 0.7, marginTop: 4 }}>{tile.subtitle}</div>}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Attendance Summary */}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
          {renderStatCard('Present', summ.present || 0, '#10B981', '+')}
          {renderStatCard('WFH', summ.wfh || 0, '#3B82F6', '~')}
          {renderStatCard('Leave', summ.leave || 0, '#F59E0B', '-')}
          {renderStatCard('Absent', summ.absent || 0, '#EF4444', '!')}
          {renderStatCard('Hours', summ.total_hours || 0, '#6366F1', '#')}
        </div>

        {/* Pending Requests */}
        {(pending.wfh > 0 || pending.travel > 0 || pending.leave > 0) && (
          <div className="card" style={{ marginBottom: 20 }}>
            <div className="card-header"><h2>Pending Requests</h2></div>
            <div className="card-body" style={{ padding: 16 }}>
              <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                {pending.wfh > 0 && (
                  <div style={{ cursor: 'pointer', padding: '8px 16px', background: '#EFF6FF', borderRadius: 8, color: '#3B82F6' }} onClick={() => setActiveTab(2)}>
                    {pending.wfh} WFH request(s) pending
                  </div>
                )}
                {pending.travel > 0 && (
                  <div style={{ cursor: 'pointer', padding: '8px 16px', background: '#FEF3C7', borderRadius: 8, color: '#D97706' }} onClick={() => setActiveTab(3)}>
                    {pending.travel} travel request(s) pending
                  </div>
                )}
                {pending.leave > 0 && (
                  <div style={{ cursor: 'pointer', padding: '8px 16px', background: '#FEF2F2', borderRadius: 8, color: '#DC2626' }} onClick={() => setActiveTab(4)}>
                    {pending.leave} leave request(s) pending
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Leave Balances */}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
          {balances.length > 0 ? balances.map(b => (
            <div key={b.id} className="card" style={{ flex: '1 0 180px', maxWidth: 240 }}>
              <div className="card-body" style={{ padding: 16 }}>
                <div style={{ fontSize: 12, color: '#64748B', marginBottom: 4 }}>{b.leave_type}</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <span style={{ fontSize: 24, fontWeight: 600, color: '#1E293B' }}>{b.balance || 0}</span>
                  <span style={{ fontSize: 12, color: '#94A3B8' }}>of {b.entitled || 0}</span>
                </div>
                <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 4 }}>Taken: {b.taken || 0}</div>
              </div>
            </div>
          )) : null}
        </div>

        {/* Announcements */}
        {announcements.length > 0 && (
          <div className="card">
            <div className="card-header"><h2>Announcements</h2></div>
            <div className="card-body" style={{ padding: 16 }}>
              {announcements.map(a => (
                <div key={a.id} style={{ padding: '12px 0', borderBottom: '1px solid #F1F5F9' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {a.priority === 'urgent' && <span className="badge badge-red">Urgent</span>}
                    {a.priority === 'high' && <span className="badge badge-yellow">Important</span>}
                    <strong style={{ color: '#1E293B' }}>{a.title}</strong>
                    <span style={{ fontSize: 11, color: '#94A3B8', marginLeft: 'auto' }}>{formatDate(a.published_at)}</span>
                  </div>
                  <div style={{ color: '#64748B', fontSize: 13, marginTop: 4 }}>{a.body}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  function renderAttendance() {
    const cal = buildCalendar();
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const todayAtt = dashboardData ? dashboardData.todayAttendance : null;

    return (
      <div>
        {renderErrorBanner()}
        {/* Check in/out bar */}
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-body" style={{ padding: 16, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ fontWeight: 600, color: '#1E293B' }}>Today: {formatDate(new Date())}</div>
            {(!todayAtt || !todayAtt.check_in) ? (
              <button className="btn btn-primary" onClick={handleCheckIn} disabled={saving}>Check In</button>
            ) : !todayAtt.check_out ? (
              <>
                <span style={{ color: '#10B981', fontSize: 13 }}>Checked in at {formatTime(todayAtt.check_in)}</span>
                <button className="btn btn-primary" style={{ background: '#EF4444' }} onClick={handleCheckOut} disabled={saving}>Check Out</button>
              </>
            ) : (
              <span style={{ color: '#10B981', fontSize: 13 }}>
                {formatTime(todayAtt.check_in)} - {formatTime(todayAtt.check_out)} ({todayAtt.hours_worked || 0}h)
              </span>
            )}
          </div>
        </div>

        {/* Month navigation and summary */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          <button className="btn btn-secondary" onClick={() => {
            const m = attMonth === 1 ? 12 : attMonth - 1;
            const y = attMonth === 1 ? attYear - 1 : attYear;
            setAttMonth(m); setAttYear(y);
          }}>Prev</button>
          <span style={{ fontWeight: 600, fontSize: 16, color: '#1E293B', minWidth: 120, textAlign: 'center' }}>
            {monthNames[attMonth - 1]} {attYear}
          </span>
          <button className="btn btn-secondary" onClick={() => {
            const m = attMonth === 12 ? 1 : attMonth + 1;
            const y = attMonth === 12 ? attYear + 1 : attYear;
            setAttMonth(m); setAttYear(y);
          }}>Next</button>

          {attSummary && (
            <div style={{ display: 'flex', gap: 12, marginLeft: 'auto', flexWrap: 'wrap' }}>
              {Object.entries({ Present: attSummary.present, WFH: attSummary.wfh, Leave: attSummary.leave, Absent: attSummary.absent, Hours: attSummary.total_hours })
                .map(([k, v]) => (
                  <span key={k} style={{ fontSize: 12, color: '#64748B' }}>{k}: <strong>{v || 0}</strong></span>
                ))}
            </div>
          )}
        </div>

        {/* Calendar grid */}
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-body" style={{ padding: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
              {dayNames.map(d => (
                <div key={d} style={{ textAlign: 'center', fontWeight: 600, fontSize: 12, color: '#64748B', padding: 4 }}>{d}</div>
              ))}
              {cal.map((cell, idx) => (
                <div key={idx} style={{
                  textAlign: 'center', padding: '8px 4px', borderRadius: 6, minHeight: 40,
                  background: cell ? cell.color + '20' : 'transparent',
                  border: cell ? `2px solid ${cell.color}` : 'none',
                }}>
                  {cell && (
                    <>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#1E293B' }}>{cell.day}</div>
                      <div style={{ fontSize: 10, color: cell.color, fontWeight: 600 }}>{cell.label}</div>
                    </>
                  )}
                </div>
              ))}
            </div>
            {/* Legend */}
            <div style={{ display: 'flex', gap: 16, marginTop: 12, flexWrap: 'wrap', fontSize: 11 }}>
              {Object.entries({ 'Present': ATT_COLORS.present, 'WFH': ATT_COLORS.wfh, 'Leave': ATT_COLORS.leave, 'Absent': ATT_COLORS.absent, 'Weekend': ATT_COLORS.weekend, 'Holiday': ATT_COLORS.holiday })
                .map(([label, color]) => (
                  <span key={label} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ width: 12, height: 12, borderRadius: 3, background: color, display: 'inline-block' }}></span>
                    {label}
                  </span>
                ))}
            </div>
          </div>
        </div>

        {/* Attendance log table */}
        <div className="card">
          <div className="card-header"><h2>Attendance Log</h2></div>
          <div className="card-body">
            {attendance.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 20, color: '#94A3B8' }}>No attendance records for this period</div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr><th>Date</th><th>Check In</th><th>Check Out</th><th>Hours</th><th>Status</th><th>Work Mode</th><th>Remarks</th></tr>
                </thead>
                <tbody>
                  {attendance.map(a => (
                    <tr key={a.id}>
                      <td>{formatDate(a.date)}</td>
                      <td>{formatTime(a.check_in)}</td>
                      <td>{formatTime(a.check_out)}</td>
                      <td>{a.hours_worked || '--'}</td>
                      <td><span className={`badge ${a.status === 'present' ? 'badge-green' : a.status === 'leave' ? 'badge-yellow' : a.status === 'absent' ? 'badge-red' : 'badge-gray'}`}>{a.status}</span></td>
                      <td>{a.work_mode || '--'}</td>
                      <td>{a.remarks || '--'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    );
  }

  function renderWfh() {
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, color: '#1E293B' }}>My WFH Requests</h3>
          <button className="btn btn-primary" onClick={() => { setErrors({}); resetDirty(); setWfhModal(true); }}>+ Request WFH</button>
        </div>

        <div className="card" style={{ marginBottom: 24 }}>
          <div className="card-body">
            {wfhRequests.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 20, color: '#94A3B8' }}>No WFH requests</div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr><th>From</th><th>To</th><th>Days</th><th>Reason</th><th>Status</th><th>Approver</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {wfhRequests.map(r => (
                    <tr key={r.id}>
                      <td>{formatDate(r.from_date)}</td>
                      <td>{formatDate(r.to_date)}</td>
                      <td>{r.days}</td>
                      <td>{r.reason || '--'}</td>
                      <td><span className={`badge ${STATUS_BADGES[r.status] || 'badge-gray'}`}>{r.status}</span></td>
                      <td>{r.approver_name || '--'}</td>
                      <td className="actions-cell">
                        {r.status === 'pending' && (
                          <button className="btn btn-secondary" style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => handleWfhCancel(r.id)}>Cancel</button>
                        )}
                        {r.rejection_reason && (
                          <span title={r.rejection_reason} style={{ cursor: 'help', color: '#EF4444', fontSize: 11 }}>Reason: {r.rejection_reason}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Team WFH Requests (manager) */}
        {teamWfh.length > 0 && (
          <div className="card">
            <div className="card-header"><h2>Team WFH Requests</h2></div>
            <div className="card-body">
              <table className="data-table">
                <thead>
                  <tr><th>Employee</th><th>From</th><th>To</th><th>Days</th><th>Reason</th><th>Status</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {teamWfh.map(r => (
                    <tr key={r.id}>
                      <td>{r.first_name ? `${r.first_name} ${r.last_name}` : (r.emp_code || '--')}</td>
                      <td>{formatDate(r.from_date)}</td>
                      <td>{formatDate(r.to_date)}</td>
                      <td>{r.days}</td>
                      <td>{r.reason || '--'}</td>
                      <td><span className={`badge ${STATUS_BADGES[r.status] || 'badge-gray'}`}>{r.status}</span></td>
                      <td className="actions-cell">
                        {r.status === 'pending' && (
                          <>
                            <button className="btn btn-primary" style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => handleWfhApprove(r.id)}>Approve</button>
                            <button className="btn btn-secondary" style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => handleWfhReject(r.id)}>Reject</button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* WFH Request Modal */}
        <Modal title="Request Work from Home" isOpen={wfhModal} onClose={() => setWfhModal(false)} size="md" dirty={dirty}>
          {errors._general && (
            <div style={{ background: '#FEF2F2', color: '#DC2626', padding: '10px 14px', borderRadius: 6, marginBottom: 16, fontSize: 13 }}>{errors._general}</div>
          )}
          <div className="form-row">
            <FormField label="From Date" name="fromDate" type="date" value={wfhForm.fromDate} onChange={autoCalcDays(wfhForm, setWfhForm, 'fromDate', 'toDate')} error={errors.fromDate} required />
            <FormField label="To Date" name="toDate" type="date" value={wfhForm.toDate} onChange={autoCalcDays(wfhForm, setWfhForm, 'fromDate', 'toDate')} error={errors.toDate} required />
          </div>
          <FormField label="Number of Days" name="days" type="number" value={wfhForm.days} onChange={(n, v) => { setWfhForm(p => ({ ...p, [n]: v })); markDirty(); }} />
          <FormField label="Reason" name="reason" type="textarea" value={wfhForm.reason} onChange={(n, v) => { setWfhForm(p => ({ ...p, [n]: v })); markDirty(); }} />
          <div className="form-actions">
            <button className="btn btn-secondary" onClick={() => setWfhModal(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleWfhSubmit} disabled={saving}>{saving ? 'Submitting...' : 'Submit Request'}</button>
          </div>
        </Modal>
      </div>
    );
  }

  function renderTravel() {
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, color: '#1E293B' }}>My Travel Requests</h3>
          <button className="btn btn-primary" onClick={() => { setErrors({}); resetDirty(); setTravelModal(true); }}>+ New Travel Request</button>
        </div>

        <div className="card" style={{ marginBottom: 24 }}>
          <div className="card-body">
            {travelRequests.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 20, color: '#94A3B8' }}>No travel requests</div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr><th>Destination</th><th>Purpose</th><th>From</th><th>To</th><th>Est. Cost</th><th>Status</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {travelRequests.map(r => (
                    <tr key={r.id}>
                      <td>{r.destination}</td>
                      <td>{r.purpose || '--'}</td>
                      <td>{formatDate(r.from_date)}</td>
                      <td>{formatDate(r.to_date)}</td>
                      <td>{formatCurrency(r.estimated_cost, r.currency)}</td>
                      <td><span className={`badge ${STATUS_BADGES[r.status] || 'badge-gray'}`}>{r.status}</span></td>
                      <td className="actions-cell">
                        <button className="btn btn-secondary" style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => {
                          setSelectedTravel(r);
                          fetchTravelExpenses(r.id);
                        }}>Expenses</button>
                        {r.status === 'approved' && (
                          <button className="btn btn-primary" style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => handleSubmitExpenses(r.id)} disabled={saving}>Submit for Reimb.</button>
                        )}
                        {r.status === 'pending' && (
                          <>
                            <button className="btn btn-primary" style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => handleTravelApprove(r.id)}>Approve</button>
                            <button className="btn btn-secondary" style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => handleTravelReject(r.id)}>Reject</button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Travel Expense Detail */}
        {selectedTravel && (
          <div className="card" style={{ marginBottom: 24 }}>
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2>Expenses: {selectedTravel.destination}</h2>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary" style={{ fontSize: 12 }} onClick={() => { setErrors({}); resetDirty(); setExpenseModal(true); }}>+ Add Expense</button>
                <button className="btn btn-secondary" style={{ fontSize: 12 }} onClick={() => { setSelectedTravel(null); setTravelExpenses([]); }}>Close</button>
              </div>
            </div>
            <div className="card-body">
              {travelExpenses.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 20, color: '#94A3B8' }}>No expenses recorded yet</div>
              ) : (
                <>
                  <table className="data-table">
                    <thead>
                      <tr><th>Type</th><th>Description</th><th>Amount</th><th>Date</th><th>Receipt</th><th>Actions</th></tr>
                    </thead>
                    <tbody>
                      {travelExpenses.map(e => (
                        <tr key={e.id}>
                          <td style={{ textTransform: 'capitalize' }}>{e.expense_type}</td>
                          <td>{e.description || '--'}</td>
                          <td>{formatCurrency(e.amount, selectedTravel.currency)}</td>
                          <td>{formatDate(e.date)}</td>
                          <td>{e.receipt_url ? <a href={e.receipt_url} target="_blank" rel="noopener noreferrer" style={{ color: '#3B82F6' }}>View</a> : '--'}</td>
                          <td className="actions-cell">
                            <button className="btn btn-secondary" style={{ fontSize: 11, padding: '2px 8px', color: '#EF4444' }} onClick={() => handleDeleteExpense(e.id)}>Delete</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div style={{ textAlign: 'right', padding: '12px 0', fontWeight: 600, color: '#1E293B' }}>
                    Total: {formatCurrency(travelExpenses.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0), selectedTravel.currency)}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Travel Request Modal */}
        <Modal title="New Travel Request" isOpen={travelModal} onClose={() => setTravelModal(false)} size="md" dirty={dirty}>
          {errors._general && (
            <div style={{ background: '#FEF2F2', color: '#DC2626', padding: '10px 14px', borderRadius: 6, marginBottom: 16, fontSize: 13 }}>{errors._general}</div>
          )}
          <FormField label="Destination" name="destination" value={travelForm.destination} onChange={(n, v) => { setTravelForm(p => ({ ...p, [n]: v })); markDirty(); }} error={errors.destination} required />
          <FormField label="Purpose" name="purpose" type="textarea" value={travelForm.purpose} onChange={(n, v) => { setTravelForm(p => ({ ...p, [n]: v })); markDirty(); }} />
          <div className="form-row">
            <FormField label="From Date" name="fromDate" type="date" value={travelForm.fromDate} onChange={(n, v) => { setTravelForm(p => ({ ...p, [n]: v })); markDirty(); }} error={errors.fromDate} required />
            <FormField label="To Date" name="toDate" type="date" value={travelForm.toDate} onChange={(n, v) => { setTravelForm(p => ({ ...p, [n]: v })); markDirty(); }} error={errors.toDate} required />
          </div>
          <div className="form-row">
            <FormField label="Estimated Cost" name="estimatedCost" type="number" value={travelForm.estimatedCost} onChange={(n, v) => { setTravelForm(p => ({ ...p, [n]: v })); markDirty(); }} />
            <FormField label="Currency" name="currency" type="select" value={travelForm.currency} onChange={(n, v) => { setTravelForm(p => ({ ...p, [n]: v })); markDirty(); }} options={['INR', 'USD', 'EUR', 'GBP']} />
          </div>
          <div className="form-actions">
            <button className="btn btn-secondary" onClick={() => setTravelModal(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleTravelSubmit} disabled={saving}>{saving ? 'Submitting...' : 'Submit Request'}</button>
          </div>
        </Modal>

        {/* Add Expense Modal */}
        <Modal title="Add Expense" isOpen={expenseModal} onClose={() => setExpenseModal(false)} size="md" dirty={dirty}>
          {errors._general && (
            <div style={{ background: '#FEF2F2', color: '#DC2626', padding: '10px 14px', borderRadius: 6, marginBottom: 16, fontSize: 13 }}>{errors._general}</div>
          )}
          <FormField label="Expense Type" name="expenseType" type="select" value={expenseForm.expenseType} onChange={(n, v) => { setExpenseForm(p => ({ ...p, [n]: v })); markDirty(); }} options={EXPENSE_TYPES} error={errors.expenseType} required />
          <FormField label="Description" name="description" value={expenseForm.description} onChange={(n, v) => { setExpenseForm(p => ({ ...p, [n]: v })); markDirty(); }} />
          <div className="form-row">
            <FormField label="Amount" name="amount" type="number" value={expenseForm.amount} onChange={(n, v) => { setExpenseForm(p => ({ ...p, [n]: v })); markDirty(); }} error={errors.amount} required />
            <FormField label="Date" name="date" type="date" value={expenseForm.date} onChange={(n, v) => { setExpenseForm(p => ({ ...p, [n]: v })); markDirty(); }} />
          </div>
          <FormField label="Receipt URL" name="receiptUrl" value={expenseForm.receiptUrl} onChange={(n, v) => { setExpenseForm(p => ({ ...p, [n]: v })); markDirty(); }} placeholder="https://..." />
          <div className="form-actions">
            <button className="btn btn-secondary" onClick={() => setExpenseModal(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleExpenseSubmit} disabled={saving}>{saving ? 'Adding...' : 'Add Expense'}</button>
          </div>
        </Modal>
      </div>
    );
  }

  function renderLeave() {
    return (
      <div>
        {/* Leave Balance Cards */}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
          {leaveBalances.length > 0 ? leaveBalances.map(b => (
            <div key={b.id} className="card" style={{ flex: '1 0 180px', maxWidth: 240 }}>
              <div className="card-body" style={{ padding: 16 }}>
                <div style={{ fontSize: 12, color: '#64748B', marginBottom: 4 }}>{b.leave_type}</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <span style={{ fontSize: 24, fontWeight: 600, color: '#1E293B' }}>{b.balance || 0}</span>
                  <span style={{ fontSize: 12, color: '#94A3B8' }}>of {b.entitled || 0}</span>
                </div>
                <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 4 }}>Taken: {b.taken || 0}</div>
              </div>
            </div>
          )) : (
            <div style={{ color: '#94A3B8', padding: 20 }}>No leave balances configured</div>
          )}
        </div>

        {/* Action bar */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
          <button className="btn btn-primary" onClick={() => { setErrors({}); resetDirty(); setLeaveModal(true); }}>+ Apply Leave</button>
        </div>

        {/* Leave Requests */}
        <div className="card">
          <div className="card-header"><h2>Leave Requests</h2></div>
          <div className="card-body">
            {leaveRequests.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 20, color: '#94A3B8' }}>No leave requests</div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr><th>Employee</th><th>Type</th><th>From</th><th>To</th><th>Days</th><th>Reason</th><th>Status</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {leaveRequests.map(r => (
                    <tr key={r.id}>
                      <td>{r.first_name ? `${r.first_name} ${r.last_name}` : (r.emp_code || '--')}</td>
                      <td>{r.leave_type}</td>
                      <td>{formatDate(r.from_date)}</td>
                      <td>{formatDate(r.to_date)}</td>
                      <td>{r.days}</td>
                      <td>{r.reason || '--'}</td>
                      <td><span className={`badge ${STATUS_BADGES[r.status] || 'badge-gray'}`}>{r.status}</span></td>
                      <td className="actions-cell">
                        {r.status === 'pending' && (
                          <>
                            <button className="btn btn-primary" style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => handleLeaveApprove(r.id)}>Approve</button>
                            <button className="btn btn-secondary" style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => handleLeaveReject(r.id)}>Reject</button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Apply Leave Modal */}
        <Modal title="Apply for Leave" isOpen={leaveModal} onClose={() => setLeaveModal(false)} size="md" dirty={dirty}>
          {errors._general && (
            <div style={{ background: '#FEF2F2', color: '#DC2626', padding: '10px 14px', borderRadius: 6, marginBottom: 16, fontSize: 13 }}>{errors._general}</div>
          )}
          <FormField label="Leave Type" name="leaveType" type="select" value={leaveForm.leaveType} onChange={(n, v) => { setLeaveForm(p => ({ ...p, [n]: v })); markDirty(); }} options={LEAVE_TYPES} />
          <div className="form-row">
            <FormField label="From Date" name="fromDate" type="date" value={leaveForm.fromDate} onChange={autoCalcDays(leaveForm, setLeaveForm, 'fromDate', 'toDate')} error={errors.fromDate} required />
            <FormField label="To Date" name="toDate" type="date" value={leaveForm.toDate} onChange={autoCalcDays(leaveForm, setLeaveForm, 'fromDate', 'toDate')} error={errors.toDate} required />
          </div>
          <FormField label="Number of Days" name="days" type="number" value={leaveForm.days} onChange={(n, v) => { setLeaveForm(p => ({ ...p, [n]: v })); markDirty(); }} error={errors.days} required />
          <FormField label="Reason" name="reason" type="textarea" value={leaveForm.reason} onChange={(n, v) => { setLeaveForm(p => ({ ...p, [n]: v })); markDirty(); }} />
          <div className="form-actions">
            <button className="btn btn-secondary" onClick={() => setLeaveModal(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleLeaveSubmit} disabled={saving}>{saving ? 'Submitting...' : 'Apply Leave'}</button>
          </div>
        </Modal>
      </div>
    );
  }

  function renderProfile() {
    if (!profile) return <div style={{ textAlign: 'center', padding: 40, color: '#94A3B8' }}>No employee profile found</div>;

    return (
      <div>
        {/* Profile Card */}
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2>My Profile</h2>
            <button className="btn btn-secondary" onClick={() => { setErrors({}); resetDirty(); setProfileEditModal(true); }}>Edit</button>
          </div>
          <div className="card-body" style={{ padding: 20 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }}>
              <div><span style={{ color: '#64748B', fontSize: 12 }}>Name</span><div style={{ fontWeight: 600 }}>{profile.first_name} {profile.last_name}</div></div>
              <div><span style={{ color: '#64748B', fontSize: 12 }}>Employee Code</span><div style={{ fontWeight: 600 }}>{profile.emp_code || '--'}</div></div>
              <div><span style={{ color: '#64748B', fontSize: 12 }}>Email</span><div style={{ fontWeight: 600 }}>{profile.email || '--'}</div></div>
              <div><span style={{ color: '#64748B', fontSize: 12 }}>Phone</span><div style={{ fontWeight: 600 }}>{profile.phone || '--'}</div></div>
              <div><span style={{ color: '#64748B', fontSize: 12 }}>Designation</span><div style={{ fontWeight: 600 }}>{profile.designation_name || '--'}</div></div>
              <div><span style={{ color: '#64748B', fontSize: 12 }}>Department</span><div style={{ fontWeight: 600 }}>{profile.department_name || '--'}</div></div>
              <div><span style={{ color: '#64748B', fontSize: 12 }}>Location</span><div style={{ fontWeight: 600 }}>{profile.location || '--'}</div></div>
              <div><span style={{ color: '#64748B', fontSize: 12 }}>Date of Joining</span><div style={{ fontWeight: 600 }}>{formatDate(profile.date_of_joining)}</div></div>
              <div><span style={{ color: '#64748B', fontSize: 12 }}>Employment Type</span><div style={{ fontWeight: 600, textTransform: 'capitalize' }}>{profile.employment_type || '--'}</div></div>
              {profile.manager && (
                <div><span style={{ color: '#64748B', fontSize: 12 }}>Reporting To</span><div style={{ fontWeight: 600 }}>{profile.manager.first_name} {profile.manager.last_name}</div></div>
              )}
            </div>
          </div>
        </div>

        {/* Current Allocations */}
        {profileAllocations.length > 0 && (
          <div className="card" style={{ marginBottom: 20 }}>
            <div className="card-header"><h2>Current Allocations</h2></div>
            <div className="card-body">
              <table className="data-table">
                <thead><tr><th>Project</th><th>Role</th><th>Allocation %</th><th>Start</th><th>End</th></tr></thead>
                <tbody>
                  {profileAllocations.map(a => (
                    <tr key={a.id}>
                      <td>{a.project_name || '--'}</td>
                      <td>{a.role || '--'}</td>
                      <td>{a.allocation_pct || 100}%</td>
                      <td>{formatDate(a.start_date)}</td>
                      <td>{formatDate(a.end_date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Skills */}
        {profileSkills.length > 0 && (
          <div className="card" style={{ marginBottom: 20 }}>
            <div className="card-header"><h2>Skills</h2></div>
            <div className="card-body">
              <table className="data-table">
                <thead><tr><th>Category</th><th>Skill</th><th>Proficiency</th><th>Experience (years)</th></tr></thead>
                <tbody>
                  {profileSkills.map(s => (
                    <tr key={s.id}>
                      <td>{s.skill_category || '--'}</td>
                      <td>{s.skill_name}</td>
                      <td>
                        <span style={{ color: ['#EF4444', '#F59E0B', '#3B82F6', '#10B981'][Math.max(0, (s.proficiency || 1) - 1)] }}>
                          {'*'.repeat(s.proficiency || 1)}
                        </span>
                        {' '}({['Beginner', 'Intermediate', 'Advanced', 'Expert'][(s.proficiency || 1) - 1]})
                      </td>
                      <td>{s.years_experience || '--'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Certifications */}
        {profileCerts.length > 0 && (
          <div className="card" style={{ marginBottom: 20 }}>
            <div className="card-header"><h2>Certifications</h2></div>
            <div className="card-body">
              <table className="data-table">
                <thead><tr><th>Certification</th><th>Issuing Body</th><th>Certified Date</th><th>Expiry Date</th></tr></thead>
                <tbody>
                  {profileCerts.map(c => (
                    <tr key={c.id}>
                      <td>{c.certification_name}</td>
                      <td>{c.issuing_body || '--'}</td>
                      <td>{formatDate(c.certified_date)}</td>
                      <td>{formatDate(c.expiry_date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Edit Profile Modal */}
        <Modal title="Edit Profile" isOpen={profileEditModal} onClose={() => setProfileEditModal(false)} size="md" dirty={dirty}>
          {errors._general && (
            <div style={{ background: '#FEF2F2', color: '#DC2626', padding: '10px 14px', borderRadius: 6, marginBottom: 16, fontSize: 13 }}>{errors._general}</div>
          )}
          <FormField label="Phone" name="phone" value={profileForm.phone} onChange={(n, v) => { setProfileForm(p => ({ ...p, [n]: v })); markDirty(); }} />
          <FormField label="Personal Email" name="email" type="email" value={profileForm.email} onChange={(n, v) => { setProfileForm(p => ({ ...p, [n]: v })); markDirty(); }} />
          <div className="form-actions">
            <button className="btn btn-secondary" onClick={() => setProfileEditModal(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleProfileUpdate} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
          </div>
        </Modal>
      </div>
    );
  }

  // ===================== Main Render =====================

  const tabRenderers = [renderDashboard, renderAttendance, renderWfh, renderTravel, renderLeave, renderProfile];

  return (
    <div>
      {/* Tab Navigation */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 20, borderBottom: '2px solid #E2E8F0', flexWrap: 'wrap' }}>
        {TABS.map((tab, idx) => (
          <button
            key={tab}
            onClick={() => { setActiveTab(idx); setErrors({}); }}
            style={{
              padding: '10px 20px',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === idx ? '2px solid #6366F1' : '2px solid transparent',
              color: activeTab === idx ? '#6366F1' : '#64748B',
              fontWeight: activeTab === idx ? 600 : 400,
              cursor: 'pointer',
              fontSize: 14,
              marginBottom: -2,
            }}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner"></div></div>
      ) : (
        tabRenderers[activeTab]()
      )}

      {/* Workflow Tile Form Modal */}
      {wfTileForm && (
        <Modal title={wfTileForm.tile.title} onClose={() => setWfTileForm(null)} width={600}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {wfTileForm.formDef && (wfTileForm.formDef.fields || []).length > 0 ? (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px' }}>
                {(wfTileForm.formDef.fields || []).map((field, i) => {
                  const fName = field.name || field;
                  const fLabel = field.label || fName.replace(/_/g, ' ');
                  const fType = field.type || 'text';
                  const inputType = fType === 'date' ? 'date' : fType === 'time' ? 'time' : fType === 'number' ? 'number' : 'text';
                  return (
                    <div key={i}>
                      <label style={{ fontSize: 12, color: '#64748B', fontWeight: 600, display: 'block', marginBottom: 4 }}>
                        {fLabel}{field.required ? ' *' : ''}
                      </label>
                      <input type={inputType}
                        style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #E2E8F0', fontSize: 13 }}
                        value={wfTileValues[fName] || ''}
                        placeholder={inputType === 'text' ? fLabel : ''}
                        onChange={e => setWfTileValues(p => ({ ...p, [fName]: e.target.value }))} />
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ color: '#64748B', fontSize: 13 }}>No form fields. Click submit to start the workflow.</div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
              <button className="btn btn-secondary" onClick={() => setWfTileForm(null)}>Cancel</button>
              <button className="btn btn-primary" disabled={saving} onClick={async () => {
                setSaving(true);
                try {
                  const res = await apiFetch('/api/workflow/instances', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      workflow_id: wfTileForm.workflow_id,
                      form_id: wfTileForm.formDef?.id,
                      form_data: wfTileValues,
                    }),
                  });
                  if (res.ok) {
                    setWfTileForm(null);
                    setWfTileValues({});
                  }
                } catch (e) { /* handled */ }
                setSaving(false);
              }}>Submit Request</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
